"use client";

import { Suspense, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@eg/ui";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useAuthStore } from "@/store/auth";
import { ApiError } from "@/lib/api";
import { useTranslations } from "@/lib/i18n/I18nProvider";

function RegisterForm() {
  const params = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const register = useAuthStore((s) => s.register);
  const isLoading = useAuthStore((s) => s.isLoading);
  const t = useTranslations();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError(t("auth.passwordMismatch"));
      return;
    }
    const callbackUrl = (params.get("callbackUrl") as string) || "/account";
    try {
      await register(email, password);
      window.location.href = callbackUrl;
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError(t("auth.registrationFailed"));
      } else {
        setError(t("auth.registrationFailedRetry"));
      }
    }
  }

  return (
    <main className="mx-auto max-w-md p-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("auth.createAccountTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label className="block mb-1">{t("auth.email")}</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div>
              <Label className="block mb-1">{t("auth.password")}</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={10}
                autoComplete="new-password"
              />
              <p className="text-xs text-gray-500">{t("auth.passwordHelp")}</p>
            </div>
            <div>
              <Label className="block mb-1">{t("auth.passwordConfirm")}</Label>
              <Input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>
            {error && <p className="text-error text-sm">{error}</p>}
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? t("auth.signingUp") : t("auth.signUp")}
            </Button>
          </form>
          <p className="text-sm text-gray-600">
            {t("auth.alreadyHaveAccount")}{" "}
            <Link href="/login" className="text-brand-700 underline">
              {t("auth.toLogin")}
            </Link>
          </p>
        </CardContent>
      </Card>
    </main>
  );
}

export default function RegisterPage() {
  return (
    <Suspense fallback={null}>
      <RegisterForm />
    </Suspense>
  );
}
